import { Controller } from "@hotwired/stimulus";

// Linear 3-step wizard for creating an entreprise from the index page.
//   0. Identité       — nom (debounced verify against existing entreprises)
//   1. Classification — type / secteur / activité
//   2. Coordonnées    — adresse, contacts, SIRET + récap
export default class extends Controller {
  static targets = [
    "form", "step", "progressStep", "progressCurrent",
    "prevBtn", "nextBtn", "submitBtn", "error",
    "nomField", "dup", "dupTitle", "dupList",
    "sirene", "sireneTitle", "sireneList", "sireneApplied", "sireneAppliedText",
    "recap", "recapName", "recapType", "recapSecteur",
  ];

  static values = {
    current:   { type: Number, default: 0 },
    verifyUrl: String,
    sireneUrl: String,
  };

  connect() {
    this._render();
    this._abort = null;
    this._sireneAbort = null;
    this._timer = null;
  }

  disconnect() {
    if (this._abort) this._abort.abort();
    if (this._sireneAbort) this._sireneAbort.abort();
    if (this._timer) clearTimeout(this._timer);
  }

  next(event) {
    event.preventDefault();
    if (!this._validateCurrent()) return;
    if (this.currentValue >= 2) return;
    this.currentValue += 1;
    this._render();
    if (this.currentValue === 2) this._refreshRecap();
  }

  prev(event) {
    event.preventDefault();
    if (this.currentValue <= 0) return;
    this.currentValue -= 1;
    this._render();
  }

  checkDuplicates() {
    if (this._timer) clearTimeout(this._timer);
    const q = this.nomFieldTarget.value.trim();
    if (q.length < 3) { this._hideDup(); this._hideSirene(); return; }
    this._timer = setTimeout(() => {
      this._fetch(q);
      this._fetchSirene(q);
    }, 350);
  }

  _fetch(q) {
    if (this._abort) this._abort.abort();
    this._abort = new AbortController();
    fetch(`${this.verifyUrlValue}?q=${encodeURIComponent(q)}`,
          { headers: { Accept: "application/json" }, signal: this._abort.signal })
      .then((r) => (r.ok ? r.json() : []))
      .then((m) => this._render_dup(m))
      .catch(() => {});
  }

  _render_dup(matches) {
    if (!Array.isArray(matches) || matches.length === 0) { this._hideDup(); return; }
    this.dupTitleTarget.textContent = matches.length === 1
      ? "Une entreprise au nom proche existe déjà :"
      : `${matches.length} entreprises au nom proche existent déjà :`;
    this.dupListTarget.innerHTML = "";
    matches.forEach((m) => {
      const li = document.createElement("li");
      li.className = "csm_dup__item";
      const meta = [m.type_entreprise, m.secteur, m.ville].filter(Boolean).join(" · ");
      li.innerHTML = `
        <span class="csm_dup__name">${this._esc(m.nom)}</span>
        ${meta ? `<span class="csm_dup__meta">${this._esc(meta)}</span>` : ""}
      `;
      this.dupListTarget.appendChild(li);
    });
    this.dupTarget.hidden = false;
  }

  _hideDup() {
    if (this.hasDupTarget) { this.dupTarget.hidden = true; this.dupListTarget.innerHTML = ""; }
  }

  _validateCurrent() {
    this._hideError();
    if (this.currentValue === 0) {
      if (!this.nomFieldTarget.value.trim()) {
        this._showError("Le nom de l'entreprise est requis.");
        this.nomFieldTarget.focus();
        return false;
      }
    }
    return true;
  }

  _render() {
    this.stepTargets.forEach((s) => {
      s.hidden = Number(s.dataset.step) !== this.currentValue;
      s.classList.toggle("esm_wizard__step--active", Number(s.dataset.step) === this.currentValue);
    });
    this.progressStepTargets.forEach((p) => {
      const i = Number(p.dataset.step);
      p.classList.toggle("esm_wizard__progress-step--active", i === this.currentValue);
      p.classList.toggle("esm_wizard__progress-step--done",   i < this.currentValue);
    });
    this.progressCurrentTarget.textContent = this.currentValue + 1;
    this.prevBtnTarget.hidden = this.currentValue === 0;
    const isLast = this.currentValue === 2;
    this.nextBtnTarget.hidden = isLast;
    this.submitBtnTarget.hidden = !isLast;
  }

  _refreshRecap() {
    if (!this.hasRecapTarget) return;
    const name = this.nomFieldTarget?.value.trim() || "—";
    this.recapNameTarget.textContent = name;
    const type = this.element.querySelector('[name="entreprise[type_entreprise]"]')?.value || "—";
    this.recapTypeTarget.textContent = type;
    const secteur = this.element.querySelector('[name="entreprise[secteur]"]')?.value || "—";
    this.recapSecteurTarget.textContent = secteur;
  }

  // ───── Recherche SIRENE (API publique recherche-entreprises) ─────

  _fetchSirene(q) {
    if (!this.hasSireneUrlValue || !this.sireneUrlValue) return;
    if (this._sireneAbort) this._sireneAbort.abort();
    this._sireneAbort = new AbortController();
    fetch(`${this.sireneUrlValue}?q=${encodeURIComponent(q)}`,
          { headers: { Accept: "application/json" }, signal: this._sireneAbort.signal })
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => this._renderSirene(list))
      .catch(() => {});
  }

  _renderSirene(results) {
    if (!this.hasSireneTarget) return;
    if (!Array.isArray(results) || results.length === 0) { this._hideSirene(); return; }
    this.sireneListTarget.innerHTML = "";
    results.forEach((r) => {
      const li = document.createElement("li");
      li.className = "csm_sirene__item";
      const bits = [];
      if (r.siren) bits.push(`SIREN ${this._esc(r.siren)}`);
      if (r.dirigeant) bits.push(this._esc(r.dirigeant));
      if (r.adresse_complete) bits.push(this._esc(r.adresse_complete));
      const closed = r.etat && r.etat !== "A"
        ? ` <span class="csm_sirene__badge">Cessée</span>` : "";
      li.innerHTML = `
        <button type="button" class="csm_sirene__btn">
          <span class="csm_sirene__name">${this._esc(r.nom || "—")}${closed}</span>
          <span class="csm_sirene__meta">${bits.join(" · ")}</span>
        </button>`;
      li.querySelector("button").addEventListener("click", () => this._applySirene(r));
      this.sireneListTarget.appendChild(li);
    });
    this.sireneTarget.hidden = false;
  }

  _applySirene(data) {
    // Champs texte simples (pas d'event pour ne pas relancer la recherche).
    this._setField("entreprise[nom]", data.nom);
    this._setField("entreprise[siret]", data.siret);
    this._setField("entreprise[adresse]", data.adresse);
    this._setField("entreprise[cp]", data.code_postal);
    this._setField("entreprise[ville]", data.ville);
    // Selects (Select2) : pays + activité NAF si on a su la mapper.
    this._setSelect("entreprise[pays]", "France");
    if (data.activite) this._setSelect("entreprise[activite]", data.activite);

    this._hideSirene();
    this._hideDup();
    if (this.hasSireneAppliedTarget) {
      const siret = data.siret ? ` · SIRET ${data.siret}` : "";
      this.sireneAppliedTextTarget.textContent = `Pré-rempli depuis SIRENE : ${data.nom || ""}${siret}`;
      this.sireneAppliedTarget.hidden = false;
    }
  }

  // Annule le pré-remplissage : vide les champs issus de SIRENE (on garde le
  // nom saisi par l'utilisateur).
  clearSirene() {
    ["entreprise[siret]", "entreprise[adresse]", "entreprise[cp]", "entreprise[ville]"]
      .forEach((n) => this._setField(n, ""));
    this._setSelect("entreprise[activite]", "");
    if (this.hasSireneAppliedTarget) this.sireneAppliedTarget.hidden = true;
  }

  _hideSirene() {
    if (this.hasSireneTarget) { this.sireneTarget.hidden = true; this.sireneListTarget.innerHTML = ""; }
  }

  _setField(name, value) {
    const el = this.element.querySelector(`[name="${name}"]`);
    if (el) el.value = value ?? "";
  }

  _setSelect(name, value) {
    const el = this.element.querySelector(`[name="${name}"]`);
    if (!el) return;
    const v = value || "";
    // L'option doit exister pour être sélectionnable (sécurité : nos valeurs
    // viennent des mêmes taxonomies que les options).
    if (v && !Array.from(el.options).some((o) => o.value === v)) {
      el.add(new Option(v, v, true, true));
    }
    el.value = v;
    const jq = window.jQuery || window.$;
    if (jq && jq.fn && jq.fn.select2 && jq(el).hasClass("select2-hidden-accessible")) {
      jq(el).val(v).trigger("change");
    } else {
      el.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  _showError(msg) { this.errorTarget.textContent = msg; this.errorTarget.hidden = false; }
  _hideError()    { this.errorTarget.hidden = true;     this.errorTarget.textContent = ""; }
  _esc(s) {
    return String(s ?? "")
      .replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;").replaceAll("'", "&#39;");
  }
}
